import classNames from "classnames";
import React, { FC, ReactNode, useState } from "react";

import {
  cleanCode,
  cleanRecoveryCode,
  recoveryCodesText,
} from "./twoStep.utils";

/** Two-step sign-in look (V49/V50: Inter, purple #6B4EFF, 14px cards). */

export const tsInput =
  "h-11 w-full rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-3.5 text-sm font-semibold text-[#1F2433] outline-none placeholder:font-medium placeholder:text-[#9097A8] focus:border-[#6B4EFF]";
export const tsPrimary =
  "flex h-[46px] w-full items-center justify-center rounded-[9px] bg-[#6B4EFF] text-[14.5px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5] disabled:cursor-not-allowed disabled:opacity-60";
export const tsSecondary =
  "flex h-10 items-center justify-center rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-4 text-[13.5px] font-bold text-[#1F2433] hover:border-[#C9D0DB] disabled:cursor-not-allowed disabled:opacity-60";
export const tsSmallPrimary =
  "flex h-10 items-center justify-center rounded-[9px] bg-[#6B4EFF] px-4 text-[13.5px] font-bold text-white hover:bg-[#5B3DF5] disabled:cursor-not-allowed disabled:opacity-60";
export const tsLink =
  "cursor-pointer border-0 bg-transparent p-0 text-[13px] font-bold text-[#6B4EFF] hover:text-[#4B2FC7] disabled:cursor-not-allowed disabled:opacity-60";

export const ShieldIcon: FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <path d={"M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"} />
    <path d={"m9 12 2 2 4-4"} />
  </svg>
);

export const TsAlert: FC<{
  children: ReactNode;
  tone?: "error" | "info" | "success" | "warning";
}> = ({ children, tone = "error" }) => (
  <div
    role={tone === "error" ? "alert" : "status"}
    className={classNames(
      "rounded-[9px] px-3.5 py-2.5 text-[13px] font-semibold leading-relaxed",
      tone === "error" && "bg-[#FDECEC] text-[#B42318]",
      tone === "info" && "bg-[#F1EEFC] text-[#4B2FC7]",
      tone === "success" && "bg-[#E9F9F1] text-[#147A4B]",
      tone === "warning" && "bg-[#FFF6E5] text-[#8A5A00]",
    )}>
    {children}
  </div>
);

/** The full-page frame of the two-step screen (shown instead of the app). */
export const TwoStepFrame: FC<{
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}> = ({ children, footer, wide }) => (
  <div
    className={
      "fixed inset-0 z-[1000] flex flex-col items-center overflow-y-auto bg-[#F5F6FA] px-4 py-10 font-[Inter,sans-serif] text-[#1F2433]"
    }>
    <div className={"my-auto flex w-full flex-col items-center gap-6"}>
      <div className={"flex items-center gap-2.5"}>
        {/* The square app icon (purple "P"); the wide wordmark was squeezed into this square (owner 2026-10-30). */}
        <img
          src={"/favicon.png"}
          alt={""}
          className={"h-9 w-9 rounded-[10px]"}
          aria-hidden={"true"}
        />
        <span className={"text-lg font-extrabold tracking-[-0.2px]"}>
          ProCharted
        </span>
      </div>
      <div
        className={classNames(
          "flex w-full flex-col gap-5 rounded-[14px] border border-[#E7E9EF] bg-white p-6 shadow-[0_8px_30px_rgba(31,36,51,0.06)] sm:p-8",
          wide ? "max-w-[520px]" : "max-w-[440px]",
        )}>
        {children}
      </div>
      {footer && (
        <div
          className={
            "flex max-w-[440px] items-center gap-2 text-center text-[12.5px] font-semibold text-[#6B7185]"
          }>
          {footer}
        </div>
      )}
    </div>
  </div>
);

export const TsHeading: FC<{ title: string; text?: ReactNode }> = ({
  title,
  text,
}) => (
  <div className={"flex flex-col gap-1.5"}>
    <span
      className={
        "mb-1 flex h-10 w-10 items-center justify-center rounded-[11px] bg-[#F0EDFF] text-[#6B4EFF]"
      }>
      <ShieldIcon size={20} />
    </span>
    <h1 className={"m-0 text-[22px] font-extrabold tracking-[-0.4px]"}>
      {title}
    </h1>
    {text && (
      <div className={"text-[13.5px] leading-[1.55] text-[#5B6678]"}>
        {text}
      </div>
    )}
  </div>
);

/** 6-digit code box (or a recovery code box). */
export const CodeInput: FC<{
  value: string;
  onChange: (value: string) => void;
  recovery?: boolean;
  autoFocus?: boolean;
  label?: string;
  id?: string;
}> = ({
  value,
  onChange,
  recovery,
  autoFocus,
  label,
  id = "two-step-code",
}) => (
  <label htmlFor={id} className={"flex flex-col gap-1.5"}>
    <span className={"text-[13px] font-bold text-[#3A4256]"}>
      {label || (recovery ? "Recovery code" : "6-digit code")}
    </span>
    <input
      id={id}
      value={value}
      autoFocus={autoFocus}
      onChange={(event) =>
        onChange(
          recovery
            ? cleanRecoveryCode(event.target.value)
            : cleanCode(event.target.value),
        )
      }
      inputMode={recovery ? "text" : "numeric"}
      autoComplete={recovery ? "off" : "one-time-code"}
      placeholder={recovery ? "ABCDE-FGHJK" : "123456"}
      maxLength={recovery ? 11 : 6}
      className={classNames(
        tsInput,
        "h-[52px] text-center text-[22px] font-extrabold tracking-[0.35em] placeholder:tracking-[0.35em] placeholder:text-[#C5CAD6]",
        recovery &&
          "text-[18px] tracking-[0.18em] placeholder:tracking-[0.18em]",
      )}
    />
  </label>
);

/** The 8 recovery codes, shown once, with Copy / Download. */
export const RecoveryCodesPanel: FC<{ codes: string[]; email: string }> = ({
  codes,
  email,
}) => {
  const [copied, setCopied] = useState(false);
  const text = recoveryCodesText(codes, email, new Date());
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const download = () => {
    try {
      const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "procharted-recovery-codes.txt";
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // download blocked: they can still copy
    }
  };
  return (
    <div className={"flex flex-col gap-3"}>
      <div
        className={
          "grid grid-cols-2 gap-x-4 gap-y-2 rounded-[11px] border border-[#EDEFF3] bg-[#FAFAFC] px-5 py-4 font-mono text-[15px] font-bold tracking-[0.06em] text-[#1F2433]"
        }
        data-testid={"recovery-codes"}>
        {codes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>
      <div className={"flex gap-2.5"}>
        <button type={"button"} className={tsSecondary} onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
        <button type={"button"} className={tsSecondary} onClick={download}>
          Download
        </button>
      </div>
    </div>
  );
};
